import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { createLogger } from "utils/logging";
import { ActivationClient, ActivationResponseType } from "utils/activation";
import { ActivationStatus, buildDetailview, type Detailview } from "./Detailview";
import type { AddToBasketEvent } from "./events/AddToBasketEvent";
import type { TrackingService } from "./tracking/TrackingService";
import type { DetailviewElement } from "./detailview_component";
import type { HTMLOcSnackbarV1Element } from "@otto-ec/otto-components/snackbar";
import { triggerHapticFeedback } from "@otto-ec/apps-api/hapticfeedback";
import { WishlistActivationBenefitType } from "./events/WishlistActivationEvent";
import { benefitEvents, ActivationTrigger, Unit } from "@otto-ec/nexus-registry/benefit/events";
import { benefitWishlist } from "@otto-ec/nexus-registry/benefit/wishlist";
import { benefitSheet } from "@otto-ec/nexus-registry/benefit/sheet";
import { defineEvent } from "@otto-ec/global-resources/nexus";
import type { HTMLFt9BenefitActivationSliderElement } from "@otto-ec/ft9-benefit-components/activation-slider";

const wait = (ms: number): Promise<void> => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};

const log = createLogger("tag", "detailview.service");

/*                                                                                              */
const ACTIVATION_TIMEOUT_LOADER = 400;

/*                                                                                      */
if (!benefitEvents.isReady) {
	benefitEvents.assignNamespace({
		active: defineEvent(),
	});
}
if (!benefitWishlist.isReady) {
	benefitWishlist.assignNamespace({
		active: defineEvent(),
	});
}

/*                                                                                       */
/*                                                                                       */
/*                                                                                               */
const sentBenefitsToWishlistConsumer = new Set<string>();

export class DetailviewService {
	constructor(private readonly trackingService: TrackingService) {}

	private readonly activationClient = new ActivationClient();

	public handleNoContentDetailview(variationId: string, customComponentElement: DetailviewElement) {
		/*                                                                                                          */
		/*                                              */
		/*                                                                                                            */
		/*                                                                                                               */
		/*            */
		/*                                                                                                            */
		/*                                        */
		/*                                                                */
		const benefitId = customComponentElement.getAutoactiveBenefitId();
		if (benefitId) {
			sendActivationEventToConsumer(benefitId, undefined, variationId, ActivationTrigger.INCORRECTLY_ASSUMED, false, "0", "percent", "");
		}
	}

	public initialize(variationId: string, customComponentElement: DetailviewElement, eventMergeId: string | null) {
		const detailviewHtmlElement = customComponentElement.querySelector<HTMLDivElement>(`[data-module="ft9-benefit-detailview-${variationId}"]`);

		if (!detailviewHtmlElement) {
			return;
		}

		/*                                          */
		const actionContainer = detailviewHtmlElement.querySelector<HTMLDivElement>(`[data-module="ft9-benefit-detailview-actions-${variationId}"]`);
		if (!actionContainer) {
			log.error("action container not found");
			return;
		}

		const detailview = buildDetailview(detailviewHtmlElement, actionContainer, variationId);
		if (!detailview) {
			return;
		}

		this.handleAlreadyActivatedBenefit(detailview, customComponentElement.getAutoactiveBenefitId());
		initExpirationHandler(detailview, customComponentElement);

		if (detailview.activationStatus === ActivationStatus.DEACTIVATED || detailview.isEarlyAccess) {
			this.handleActivationFromOtherSources(detailview, customComponentElement);
		}

		log.info("Send load tracking event and set visible handler");
		this.trackingService.sendDetailviewBenefitLoadedEvent(detailview, eventMergeId);
		const toAppButtonElement = customComponentElement.querySelector<HTMLElement>("#benefit-detailview_app_btn");
		if (toAppButtonElement) {
			toAppButtonElement.addEventListener("click", () => {
				this.trackingService.sendDetailviewBenefitClickEvent(detailview);
			});
		}

		eventQBus.on("ft1.order-core.addToBasketResult", addToBasketHandler(this.trackingService, detailview).bind(this));

		const { activate } = window.otto.ft9.benefit.components.activationSlider;

		return activate.subscribe((event) => {
			const sliderInstance = event.instance as HTMLFt9BenefitActivationSliderElement;
			if (!detailviewHtmlElement.contains(sliderInstance)) return;
			if (sliderInstance.dataset.benefitId !== detailview.benefitId) return;

			const activationUrl = sliderInstance.dataset.activationUrl;
			if (!activationUrl) {
				log.error("Missing activationUrl");
				return;
			}

			const errorLoggingPayload = {
				benefit_id: detailview.benefitId,
				benefit_active_until: detailview.activeUntil,
			};

			const startedMs = new Date().getMilliseconds();

			this.activationClient.activateByUrl(activationUrl, errorLoggingPayload, "tag").then(async (activationResponse) => {
				const endedMs = new Date().getMilliseconds();

				/*                                                                                                                                                */
				const LOADING_STATE_TIMEOUT_MS = Math.max(0, ACTIVATION_TIMEOUT_LOADER + 200 - (endedMs - startedMs));

				const eventMergeId = this.trackingService.sendDetailviewBenefitSwipeEvent(detailview, activationResponse);

				switch (activationResponse) {
					case ActivationResponseType.Activated:
						await wait(LOADING_STATE_TIMEOUT_MS);
						sliderInstance.setAttribute("is-activated", "true");
						detailview.activationStatus = ActivationStatus.ACTIVATED;
						setActivatedHeadline(detailviewHtmlElement);

						triggerHapticFeedback();
						sendActivationEventToConsumer(detailview.benefitId, detailview.frameId, variationId, ActivationTrigger.ACTIVATED_BY_USER, true, detailview.value, detailview.unit, detailview.displayName, eventMergeId);
						sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true, eventMergeId);
						return;
					case ActivationResponseType.Conflict: {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--conflict");
						if (snackbar) {
							snackbar.open = true;
						}

						await wait(LOADING_STATE_TIMEOUT_MS);
						sliderInstance.reset();
						return;
					}
					case ActivationResponseType.Error: {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--generic-error");
						if (snackbar) {
							snackbar.open = true;
						}

						await wait(LOADING_STATE_TIMEOUT_MS);
						sliderInstance.reset();
						return;
					}
				}
			});
		});
	}

	private handleAlreadyActivatedBenefit(detailview: Detailview, benefitIdAlreadyActivatedByFT5: string | undefined) {
		if (detailview.activationStatus === ActivationStatus.ACTIVATED) {
			sendActivationEventToConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.FROM_DB_STORAGE, true, detailview.value, detailview.unit, detailview.displayName);
		}
		if (benefitIdAlreadyActivatedByFT5 && detailview.activationStatus === ActivationStatus.DEACTIVATED) {
			/*                                                                                                          */
			/*                                              */
			/*                                                                                                            */
			/*                                                                                                               */
			/*            */
			/*                                                                                                             */
			/*                                        */
			/*                                                                */
			sendActivationEventToConsumer(benefitIdAlreadyActivatedByFT5, undefined, detailview.variationId, ActivationTrigger.INCORRECTLY_ASSUMED, false, detailview.value, detailview.unit, detailview.displayName);
		}
	}

	private handleActivationFromOtherSources(detailview: Detailview, detailviewElement: DetailviewElement) {
		/*                      */
		benefitSheet.active.subscribe(({ benefitId, reloadDelay }) => {
			if (!benefitId || (benefitId !== detailview.benefitId && benefitId !== detailview.frameId)) return;
			sendActivationEventToConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.ACTIVATED_ON_SHEET, true, detailview.value, detailview.unit, detailview.displayName);
			sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true);

			setTimeout(async () => {
				await detailviewElement.triggerReload();
			}, reloadDelay ?? 500); /*                                                             */
			/*                                                                                                            */
			/*                                                                                                             */
			/*                                                                                                          */
		});

		/*                                 */
		benefitEvents.active.subscribe(({ id, frameId, active, variationId, trigger }) => {
			if (!active || trigger !== ActivationTrigger.ACTIVATED_BY_USER) return;
			if (!variationId || variationId === detailview.variationId) return;

			const benefitId = id;

			const container = detailviewElement.querySelectorAll<HTMLDivElement>(`[data-module|="ft9-benefit-detailview-actions"]`);
			container.forEach((element) => {
				const benefitIdFromContainer = element.dataset.benefitId!;
				const frameIdFromContainer = element.dataset.frameId;

				const isSameBenefitId = benefitIdFromContainer === benefitId;
				const isSameFrameId = !!(frameIdFromContainer && frameId && frameIdFromContainer === frameId);

				if (isSameBenefitId || isSameFrameId) {
					const activationSlider = element.querySelector("ft9-benefit-activation-slider-v1");
					if (activationSlider && !activationSlider.hasAttribute("is-activated")) {
						activationSlider.setAttribute("is-activated", "true");
						detailview.activationStatus = ActivationStatus.ACTIVATED;
						setActivatedHeadline(detailviewElement);
						sendActivationEventToConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.ACTIVATED_ON_OTHER_VARIATION, true, detailview.value, detailview.unit, detailview.displayName);
					}
				}
			});
		});
	}
}

function initExpirationHandler(detailview: Detailview, detailviewElement: DetailviewElement) {
	detailviewElement.expiry?.scheduler(detailview.activeUntil, async () => {
		/*                                                                                */
		if (detailview.isEarlyAccess) {
			await wait(1_000);
			await detailviewElement.triggerReload();
			return;
		}

		detailview.activationStatus = ActivationStatus.DEACTIVATED;
		sendActivationEventToConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.EXPIRED_BENEFIT, false, detailview.value, detailview.unit, detailview.displayName);
		sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, false);

		await wait(10_000);
		await detailviewElement.triggerReload();
	});
}

/*                                                            */
function sendActivationEventToConsumer(benefitId: string, frameId: string | undefined, variationId: string, trigger: ActivationTrigger, active: boolean, value: string, unit: string, displayName: string, eventMergeId?: string) {
	const parsedValue = Number.parseInt(value);

	let parsedUnit: Unit;
	if (unit === "Euro") {
		parsedUnit = Unit.Euro;
	} else {
		parsedUnit = Unit.Percent;
	}

	const event = {
		id: benefitId, /*                     */
		frameId: frameId, /*          */
		active: active,
		variationId: variationId, /*                     */
		trigger: trigger,
		value: parsedValue, /*          */
		unit: parsedUnit, /*          */
		displayName, /*          */
		eventMergeId, /*          */
	};
	log.debug("Send event", event);
	benefitEvents.active.emit(event);
}

/*  */
/*                                                                         */
export function sendActivationEventToWishlistConsumer(benefitId: string, frameId: string | undefined, active: boolean, eventMergeId?: string) {
	const activatedId = frameId || benefitId;
	if (sentBenefitsToWishlistConsumer.has(activatedId)) return;

	let type: WishlistActivationBenefitType;
	if (frameId === undefined) {
		type = WishlistActivationBenefitType.PARTNER;
	} else {
		type = WishlistActivationBenefitType.PLATFORM;
	}

	benefitWishlist.active.emit({
		id: activatedId,
		type,
		active: active,
		eventMergeId,
	});
	sentBenefitsToWishlistConsumer.add(activatedId);
}

export function addToBasketHandler(trackingService: TrackingService, detailview: Detailview) {
	return (event: AddToBasketEvent) => {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		if (event.variationId !== detailview.variationId) {
			/*                                                                               */
			/*                                                                                                 */
			return;
		}
		trackingService.sendDetailviewBenefitMergeEvent(detailview, event.eventMergeId);
	};
}

function setActivatedHeadline(detailviewHtmlElement: HTMLElement) {
	const headlineElement = detailviewHtmlElement.querySelector<HTMLElement>("[data-headline-activated]");
	if (!headlineElement) return;
	headlineElement.textContent = headlineElement.dataset.headlineActivated!;
}
